/**
 * Main-thread handle on the store.
 *
 * Every method resolves only after the worker has confirmed the write committed to
 * SQLite. Nothing here optimistically resolves, and nothing here retries silently.
 * If a write fails, the caller gets a rejection and the UI has to say so.
 */
import type { Req, Res, Capabilities, SelfTestResult, KvRow, PortableRow, ExportDoc } from './protocol.ts';
import { getDeviceId } from './deviceId.ts';

type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void };

export class StoreUnavailable extends Error {}

export class Store {
  private worker: Worker | null = null;
  private readonly pending = new Map<number, Pending>();
  private seq = 0;
  private ready: Promise<Capabilities> | null = null;

  readonly deviceId = getDeviceId();
  /** Set once init settles. Read it rather than assuming the store works. */
  capabilities: Capabilities | null = null;
  /** Set to receive long-running-operation progress. */
  onProgress: ((stage: string) => void) | null = null;
  openError: string | null = null;

  open(): Promise<Capabilities> {
    if (this.ready) return this.ready;
    this.ready = (async () => {
      this.worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = (ev: MessageEvent<Res>) => {
        const msg = ev.data;
        if (msg.kind === 'progress') { this.onProgress?.(msg.stage); return; }
        const p = this.pending.get(msg.id);
        if (!p) return;
        this.pending.delete(msg.id);
        if (msg.kind === 'ok') p.resolve(msg.value);
        else p.reject(new StoreUnavailable(msg.error));
      };
      this.worker.onerror = (ev) => {
        const err = new StoreUnavailable(ev.message || 'the database worker crashed');
        this.openError = err.message;
        for (const [, p] of this.pending) p.reject(err);
        this.pending.clear();
      };
      try {
        const caps = await this.call<Capabilities>('init', [this.deviceId]);
        this.capabilities = caps;
        return caps;
      } catch (e) {
        this.openError = e instanceof Error ? e.message : String(e);
        // Try to recover the probe result even though the database did not open,
        // so the Data screen can explain the failure rather than just showing nothing.
        try { this.capabilities = await this.call<Capabilities>('capabilities', []); } catch { /* nothing to add */ }
        throw e;
      }
    })();
    return this.ready;
  }

  private call<T>(method: Req['method'], args: unknown[]): Promise<T> {
    const w = this.worker;
    if (!w) return Promise.reject(new StoreUnavailable('the database worker is not running'));
    const id = ++this.seq;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      w.postMessage({ id, method, args } as Req);
    });
  }

  put(key: string, value: unknown)      { return this.call<KvRow>('put', [key, value]); }
  del(key: string)                      { return this.call<KvRow>('del', [key]); }
  setFlag(key: string, on: boolean)     { return this.call<KvRow>('setFlag', [key, on]); }
  get<T>(key: string)                   { return this.call<T | undefined>('get', [key]); }
  rowFor(key: string)                   { return this.call<KvRow | undefined>('rowFor', [key]); }
  rows()                                { return this.call<KvRow[]>('rows', []); }
  stats()                               { return this.call<Stats>('stats', []); }
  integrityCheck()                      { return this.call<string>('integrityCheck', []); }
  pragmas()                             { return this.call<Record<string, string>>('pragmas', []); }
  unsynced(limit?: number)              { return this.call<PortableRow[]>('unsynced', [limit]); }
  markSynced(rows: Pick<PortableRow, 'k' | 'updated_at'>[]) { return this.call<number>('markSynced', [rows]); }
  unsyncedCount()                       { return this.call<number>('unsyncedCount', []); }
  async markAllUnsynced()               { await this.call<null>('markAllUnsynced', []); }
  getMeta(k: string)                    { return this.call<string | undefined>('getMeta', [k]); }
  async setMeta(k: string, v: string)   { await this.call<null>('setMeta', [k, v]); }
  exportJson()                          { return this.call<ExportDoc>('exportJson', []); }
  importMerge(doc: ExportDoc)           { return this.call<{ applied: number; ignored: number }>('importMerge', [doc]); }
  importReplace(doc: ExportDoc)         { return this.call<{ applied: number }>('importReplace', [doc]); }
  wipe()                                { return this.call<null>('wipe', []); }
  selfTest()                            { return this.call<SelfTestResult[]>('selfTest', []); }

  async getPrefix<T>(prefix: string): Promise<Map<string, T>> {
    return new Map(await this.call<[string, T][]>('getPrefix', [prefix]));
  }
}

export interface Stats {
  rowCount: number; liveRowCount: number; tombstoneCount: number;
  unsynced: number; lastWriteAt: number | null; pageBytes: number | null;
}

export const store = new Store();
export type { Capabilities, SelfTestResult, KvRow, ExportDoc };
